import { MARKET, MEGASTRUCTURE } from '@sd/content';
import {
  capitalHex,
  hexDistance,
  hexKey,
  isPassable,
  type GameState,
  type Hex,
  type Command,
  type PlayerState,
} from '@sd/sim';
import { at, canBuy, caravanFleets, gsCash, orderShip, sendTo, warFleets } from './fleets';
import type { Bot } from './match';
import { canBuildSwarm } from './military';

/** The free corona hex nearest a player's capital, for its Dyson swarm. */
export function swarmSite(state: GameState, playerId: string): Hex | null {
  const from = capitalHex(state, playerId);
  const taken = new Set(state.megastructures.map((m) => hexKey(m.hex)));
  const sites = state.system.coronaHexes
    .filter((h) => isPassable(state.system, h) && !taken.has(hexKey(h)))
    .sort(
      (a, b) => hexDistance(from, a) - hexDistance(from, b) || hexKey(a).localeCompare(hexKey(b)),
    );
  return sites[0] ?? null;
}

/** Whether a player keeps its transports for the swarm rather than for outposts. */
export const savesTransportForSwarm = (state: GameState, playerId: string): boolean => {
  const self = state.players.find((p) => p.id === playerId);
  return !!self && canBuildSwarm(self) && !state.megastructures.some((m) => m.owner === playerId);
};

/** The next stage's missing alloys or exotics, as much as the treasury buys now; else null. */
function stageShopping(state: GameState, self: PlayerState): Command | null {
  const cost = MEGASTRUCTURE.stageCost;
  // Alloys first: the capital's factories eat exotics, so those are bought last.
  for (const good of ['alloys', 'exotics'] as const) {
    const missing = Math.ceil(cost[good] - self.goods[good]);
    if (missing <= 0) continue;
    if (!canBuy(state, self.id, good)) return null;
    const unit = state.market[good].price * (1 + MARKET.fee) * 1.15;
    const quantity = Math.min(200, missing, Math.floor((gsCash(self) * 0.9) / unit));
    return quantity >= 1 ? { type: 'marketBuy', playerId: self.id, good, quantity } : null;
  }
  return null;
}

/** Whether the swarm is under way and still needs its stages: the treasury goes there. */
export const savingForSwarm = (state: GameState, me: PlayerState): boolean =>
  canBuildSwarm(me) &&
  !state.megastructures.some((m) => m.owner === me.id && m.stages >= MEGASTRUCTURE.stages);

/** What the scientist keeps in stock while it saves for the swarm: one stage. */
export const swarmStock = (state: GameState, me: PlayerState) =>
  savingForSwarm(state, me) ? MEGASTRUCTURE.stageCost : {};

/**
 * Technological victory (GDD §10): once «Управление звездой» is known, a
 * transport flies to the corona and starts the Dyson swarm; warships guard
 * the site. Stages follow on their own while alloys and exotics last, so it
 * buys what they lack.
 */
export const builderBot: Bot = (state, playerId, rng) => {
  if (state.pause || !rng.chance(0.05)) return [];
  const self = state.players.find((p) => p.id === playerId);
  if (!self || !canBuildSwarm(self)) return [];
  const mine = state.megastructures.find((m) => m.owner === playerId);
  if (mine) {
    // Guard the site and feed the stages.
    const guard = warFleets(state, playerId).find((f) => !at(f, mine.hex));
    const move = guard && sendTo(playerId, guard, mine.hex);
    if (move) return [move];
    if (mine.stages < MEGASTRUCTURE.stages && mine.buildingUntil === null) {
      const buy = stageShopping(state, self);
      if (buy) return [buy];
    }
    return [];
  }
  const site = swarmSite(state, playerId);
  if (!site) return [];
  const caravans = caravanFleets(state);
  const carrier = state.fleets.find(
    (f) =>
      f.owner === playerId && !caravans.has(f.id) && f.ships.some((s) => s.cls === 'transport'),
  );
  if (!carrier) {
    if (self.shipyard.some((o) => o.ship === 'transport')) return [];
    const order = orderShip(self, 'transport', 300);
    return order ? [order] : [];
  }
  if (at(carrier, site)) {
    const buy = stageShopping(state, self);
    if (buy) return [buy];
    const cost = MEGASTRUCTURE.stageCost;
    if (self.goods.alloys < cost.alloys || self.goods.exotics < cost.exotics) return [];
    return [{ type: 'buildMegastructure', playerId, fleetId: carrier.id }];
  }
  const move = sendTo(playerId, carrier, site);
  return move ? [move] : [];
};
